import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import UserService from "rc-app/services/UserService";
import { getPublicProject } from "rc-app/api/shareLinkActionsApi";
import {
  getGuidedRoom,
  getGuidedRoomLobby,
  admitToGuidedRoom,
  manageParticipant,
  endGuidedRoom,
  patchGuidedRoom,
  startGuidedRoomRecording,
  stopGuidedRoomRecording,
  listGuidedRoomRecordings,
} from "rc-app/api/guidedRoomActionsApi";
import { useGuidedRoomVoice } from "rc-app/hooks/useGuidedRoomVoice";
import { connectionNotice } from "rc-app/utils/guidedRoomVoice";
import { useGuidedRoomRelay } from "rc-app/hooks/useGuidedRoomRelay";
import { canvasUrl, primarySource } from "rc-app/utils/guidedRoomCanvas";
import { useGuidedRoomChat } from "rc-app/hooks/useGuidedRoomChat";
import { chatTime } from "rc-app/utils/guidedRoomChat";
import {
  applyHand,
  pruneHands,
  handQueue,
  handWaitLabel,
  type Hands,
} from "rc-app/utils/guidedRoomRelay";
import { apiMessage } from "rc-app/utils/guidedRoomFlow";
import { useViewportInsets } from "rc-app/hooks/useViewportInsets";
import {
  recordingDock,
  recordingProblem,
  latestRecording,
  autoAdmitSetting,
  AUTO_ADMIT_KEY,
  type RecordingBrief,
} from "rc-app/utils/guidedRoomRecording";
import "./GuidedRoomHost.css";

/* ── Tour Room (Host) ──────────────────────────────────────────────────
   Route: /room/:roomId    (staff only — inside RenderOnAuthenticated)

   Built to docs/product specs/tour-room-host/Tour Room Host.html + its
   spec. Everything floats over a full-bleed stage; there is no page
   scroll.

   ── AUTH NOTE ──
   Every call this page makes (lobby, admit, manage, end, patch) is
   host-only and authorised by the KEYCLOAK TOKEN, not by the participant
   credential. So the page works standalone on a cold refresh — the host
   does NOT need the session_id/session_secret pair to run the lobby.
   That credential matters only for the host's own RTC token, which is
   still a stub, so it's read opportunistically and never blocks.

   ── WHAT IS LIVE vs WAITING ON A BACKEND ──
   Live against Nivedita's API: the lobby, admit, deny, mute, unmute,
   remove, mute-all, start/stop, end, the join link.

   VOICE IS LIVE as of 2026-08-20 — rtc_token mints real Agora tokens, so
   the mic works and remote participants are audible. See
   hooks/useGuidedRoomVoice.ts. The mic needs the host CREDENTIAL, unlike
   every other control on this page, so it degrades to disabled on a cold
   refresh in a browser that never created the room.

   Rendered but DISABLED, each with a tooltip naming what it waits on —
   deliberately not faked, because a control that looks like it works and
   silently reaches nobody is worse than one that says it isn't ready:
     · camera                             → deliberate, not blocked: a tour
                                            is a walkthrough with a voice
                                            over it, and faces compete with
                                            the property
     · laser pointer                      → a pointer channel (rides the
                                            Agora data stream, per the
                                            2026-08-12 architecture call)
     · chat                               → same data stream
     · record                             → start/stop actions + a flag on
                                            the room doc so every client
                                            can render the live indicator

   ── PARTICIPANT MUTE IS NOT VOICE MUTE ──
   The mute button on a participant row calls the backend's `manage`
   action; it is moderation state, not an Agora operation. Muting someone
   else's actual microphone has to be a message THEY act on, and that
   needs the data channel the laser and chat are also waiting for. Until
   then a muted participant is muted in the room's records and still
   audible, which is a gap worth knowing about before a real tour.

   ── ROLE FIDELITY (a real gap, see roleOf) ──
   The design distinguishes SIX roles; the lobby payload carries three.
   The finer ones degrade to the coarse label rather than being guessed.
*/

/* Who's looking at this page.
     host  — created the room; runs the lobby, can end it
     staff — a colleague on the room's invite list; enters directly (no
             lobby, no OTP) but drives nothing
     null  — authenticated, but not on this room's list → refused

   Staff never take the /visit OTP path. They already hold a Keycloak
   account, and sending them through the buyer flow would have created a
   LEAD row for a colleague (case C3) — a nasty way to pollute the CRM. */
type ViewerRole = "host" | "staff" | null;

type LobbyStatus = "WAITING" | "ADMITTED" | "LEFT" | "REJECTED" | string;

/* The six roles the design paints. Colour carries role consistently
   across avatars, tags and the role prefix in a detail line:
   emerald = host/invited, sky = uninvited, amber = new lead / family,
   lilac = staff.

   `auto` is documentation ONLY. Admission is decided server-side and
   read off lobby_status — the prototype's client-side auto-admit loop is
   deliberately NOT ported, because a page that decides its own admissions
   is exactly the hole the spec warns about ("never trust the client for
   admission"). It's recorded here so the two files still agree on which
   roles are supposed to walk in. */
type RoleKey = "host" | "lead" | "unlead" | "fresh" | "staff" | "guest";
const R: Record<RoleKey, { l: string; c: string; a: string; auto: boolean }> = {
  host:   { l: "Host",           c: "host",   a: "",    auto: true },
  lead:   { l: "Invited lead",   c: "lead",   a: "",    auto: true },
  guest:  { l: "Family pass",    c: "guest",  a: "warm", auto: true },
  staff:  { l: "Staff",          c: "staff",  a: "lil",  auto: true },
  unlead: { l: "Uninvited lead", c: "unlead", a: "sky",  auto: false },
  fresh:  { l: "New lead",       c: "fresh",  a: "warm", auto: false },
};

type Participant = {
  session_id: string;
  /* What Agora calls this person. Identical to session_id while
     GUIDED_RTC_UID_MODE=account; a number once it is flipped to int. The
     speaking ring is keyed on it, so reading session_id there would go
     dark for everyone on the flip. See PersonRow in utils/guidedRoomGuest. */
  rtc_uid: string;
  display_name: string;
  role: RoleKey;
  /* Verbatim from the backend — a phone number, "Family of Meera Iyer",
     or a staff job title. NEVER regex a role out of this string: an
     earlier build did, and it ate staff titles ("System admin · Opensky
     Demo" → "Opensky Demo"). The role is prepended as its own <span>. */
  detail: string;
  lobby_status: LobbyStatus;
  muted?: boolean;
  /* Set only by the localhost tier preview. A demo row has no server-side
     counterpart, so its actions resolve locally instead of 404ing. */
  __demo?: boolean;
};

const HOST_CRED_KEY = "guidedRoom:host:v1";
/* Both generations. v2 is keyed by host, v1 was keyed by project — a host
   who ends a room created before the change must still get their claim
   back, so the sweep covers the old prefix too. */
const ACTIVE_KEY = "guidedRoom:active:v2";
const LEGACY_ACTIVE_KEY = "guidedRoom:active:v1";
const POLL_MS = 4000;

const IS_LOCAL =
  typeof window !== "undefined" &&
  /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname);

/* Hand the host their next room back when this tour ends, so the Guided
   Tour button on the project page stops saying "live".
   The marker is keyed by HOST, and this page only knows the room, so sweep
   for the entry pointing at us rather than threading the host id through
   the URL. Sweeping by VALUE also means we release the claim no matter
   which key generation wrote it. */
const releaseRoom = (roomId: string) => {
  try {
    localStorage.removeItem(`${HOST_CRED_KEY}:${roomId}`);
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (!k) continue;
      if (!k.startsWith(`${ACTIVE_KEY}:`) && !k.startsWith(`${LEGACY_ACTIVE_KEY}:`)) continue;
      try {
        if (JSON.parse(localStorage.getItem(k) || "null")?.room_id === roomId) {
          localStorage.removeItem(k);
        }
      } catch { /* malformed entry — leave it, the verify pass will clear it */ }
    }
  } catch { /* storage unavailable — the project page re-verifies anyway */ }
};

/* ⚠️ BACKEND GAP — the finer roles don't exist yet.
   The lobby returns `role` as host | lead | guest. The design needs to
   tell an INVITED lead (emerald, walked straight in) from an UNINVITED
   one who got a forwarded link (sky) from a number we've never seen
   (amber, the one arrival worth flagging).

   So: use the finer value when the payload offers one — §9 of the spec
   asks for `waiting[].source` — and otherwise fall back to the coarse
   role. Do NOT infer it from lobby_status: "waiting, therefore
   uninvited" reads plausibly and is wrong for anyone the host admitted
   by hand, and mislabelling whose lead someone is has consequences well
   past this screen. A coarse label is a smaller error than a confident
   wrong one. */
const roleOf = (raw: any, ctx?: InviteCtx): RoleKey => {
  const fine = String(raw?.source || raw?.role_detail || raw?.lead_kind || "")
    .toLowerCase().replace(/[\s-]/g, "_");
  if (fine) {
    if (fine.includes("uninvited") || fine.includes("forward")) return "unlead";
    if (fine.includes("new") || fine.includes("fresh") || fine.includes("captur")) return "fresh";
    if (fine.includes("invited")) return "lead";
    if (fine.includes("staff") || fine.includes("agent") || fine.includes("admin")) return "staff";
    if (fine.includes("guest") || fine.includes("family")) return "guest";
  }
  const coarse = String(raw?.role || "lead").toLowerCase();
  if (coarse === "host") return "host";
  if (coarse === "guest") return "guest";
  if (coarse === "staff") return "staff";

  /* A buyer. Invited or not comes from the room's OWN invite list — the
     `invitees` array plus its `settings.invitees` mirror, both of which
     this page already holds. That is the same list the host built in the
     create modal, so it is authoritative for display.

     Display only. Admission stays server-side, exactly as before. */
  if (ctx?.isInvited?.(raw)) return "lead";

  /* ── KNOWN CONTACT vs BRAND NEW NUMBER ──
     The note that used to sit here said an unmatched buyer stays
     "Uninvited lead" until `is_existing_lead` ships. It has shipped — the
     lobby computes it on every read, comparing the customer's created_at
     against the room's, and returns both `is_existing_lead` (boolean) and
     `contact` ("known" | "new"). This screen simply never read them, which
     is why a first-time caller was labelled as someone already on the
     agency's book.

     That difference is the most useful thing on this card. "Uninvited
     lead" means a contact we have who forwarded themselves in; "New lead"
     means a number nobody has ever seen, arriving live — the single most
     interesting admission a host will make all day.

     Both fields are read because the backend sends both, and a boolean
     that arrives as the string "false" would silently invert the meaning
     if only the loose check existed. */
  if (raw?.is_existing_lead === false || raw?.contact === "new") return "fresh";
  if (raw?.is_existing_lead === true || raw?.contact === "known") return "unlead";

  /* Neither field present — an older row, or a shape we don't recognise.
     Not-on-the-list is still a fact we can prove; known-vs-new is not. */
  return ctx ? "unlead" : "lead";
};

/* "98765 43210" → "+91 98765 43210". Anything that isn't a plain ten
   digits is passed through untouched — a half-formatted number is worse
   than a raw one. */
const prettyPhone = (raw: string, cc?: string): string => {
  const d = String(raw || "").replace(/\D/g, "");
  if (d.length !== 10) return String(raw || "");
  return `${cc || "+91"} ${d.slice(0, 5)} ${d.slice(5)}`;
};

/* The lobby response shape isn't pinned down in either spec doc, and the
   endpoint predates this page. Rather than bet on one shape, accept the
   three plausible ones and partition on lobby_status ourselves — a wrong
   guess here would silently render an empty lobby, which is the single
   worst failure mode for this screen. */
/* Everything the normaliser needs that lives outside the lobby response:
   who the host built the room for, and who the host is. */
type InviteCtx = {
  isInvited: (raw: any) => boolean;
  /* A lead id → the name the host typed when they built the room. The
     lobby returns `invited_by` as the ISSUER'S LEAD ID and no name, so
     without this the family rows read "Family of 6a97a5829449b425f571a092"
     — a raw database id, on the host's screen, during a live tour. Seen
     2026-09-10. The host already holds the names in settings.invitees;
     nothing needs to be fetched. */
  nameForId: (id: string) => string;
  selfName: string;
  selfTitle: string;
};

/* 24 hex characters. Used to make sure an id can never reach the screen
   even if some other field arrives carrying one. */
const looksLikeObjectId = (s: string): boolean => /^[a-f0-9]{24}$/i.test(String(s || "").trim());

const normalizeLobby = (res: any, ctx?: InviteCtx): Participant[] => {
  const rows: any[] = Array.isArray(res)
    ? res
    : res?.participants || res?.lobby ||
      [...(res?.waiting || []), ...(res?.admitted || [])];
  if (!Array.isArray(rows)) return [];
  return rows
    .map((r: any) => {
      const role = roleOf(r, ctx);
      /* Prefer a name the backend sent; otherwise resolve the issuer's
         lead id against the invite list the host built. Anything still
         unresolved is dropped rather than shown — "Family" alone is a
         true and useful label, an ObjectId is neither. */
      const invitedByRaw = String(r?.invited_by_name || r?.invited_by || "").trim();
      const resolved = ctx?.nameForId?.(invitedByRaw) || "";
      const invitedBy = resolved
        || (invitedByRaw && !looksLikeObjectId(invitedByRaw) ? invitedByRaw : "");
      const phone = r?.mobile_number || r?.phone || r?.mobile || "";
      /* Precedence matters: a family pass is better described by who
         vouched for them than by a number they never gave us, and staff
         by their job title. The host is "You · agent" — the design never
         shows the host their own phone number. */
      const detail =
        r?.detail ||
        (role === "host" && ctx?.selfTitle ? `You · ${ctx.selfTitle}` : "") ||
        (role === "guest" && invitedBy ? `Family of ${invitedBy}` : "") ||
        (role === "staff" ? (r?.title || r?.agency_role || "") : "") ||
        (phone ? prettyPhone(phone, r?.country_code) : "");
      return {
        session_id: r?.session_id || r?.id || "",
        rtc_uid: String(r?.rtc_uid || r?.session_id || r?.id || ""),
        /* The host's own row often comes back with no name — the backend
           knows them by token, not display_name — and the generic "Guest"
           fallback then labelled the host "Guest", which is both wrong and
           the name of a different role on this screen. Their own name is
           on the Keycloak token, so use it. */
        display_name:
          r?.display_name || r?.first_name || r?.name ||
          (role === "host" ? ctx?.selfName : "") || "Guest",
        role,
        detail,
        /* A row that arrived under `waiting`/`admitted` may carry no status
           of its own — infer it from the bucket it came in. */
        lobby_status: String(
          r?.lobby_status || r?.status ||
          ((res?.waiting || []).includes(r) ? "WAITING" : "ADMITTED"),
        ).toUpperCase(),
        muted: !!r?.muted,
      } as Participant;
    })
    .filter((p: Participant) => p.session_id);
};

const initial = (name: string) => (name.trim()[0] || "?").toUpperCase();
const firstNameOf = (name: string) => name.trim().split(/\s+/)[0] || name;

/* The backend sends naive timestamps with no TZ marker, representing UTC.
   JS parses a bare "2026-08-14T10:00:00" as LOCAL time, so on an IST
   machine a room that started 90 seconds ago read as 5:31:42 — the 5:30
   offset plus the real elapsed time. Same idiom AnalyticsV3 uses. */
const utcDate = (ts: string): number => {
  if (!ts) return NaN;
  const hasTz = /Z|[+-]\d{2}:?\d{2}$/.test(ts);
  return new Date(hasTz ? ts : ts.replace(" ", "T") + "Z").getTime();
};

/* Rolls over into hours. An earlier mm:ss-only version rendered a long
   room as "333:24" — guided tours do run past the hour, and a minute count
   in the hundreds reads as a bug to the host mid-tour. */
const clock = (totalSeconds: number) => {
  const s = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(s / 3600);
  const mm = String(Math.floor((s % 3600) / 60)).padStart(2, "0");
  const ss = String(s % 60).padStart(2, "0");
  return h ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
};

/* ── icons ───────────────────────────────────────────────────────────── */
const Svg: React.FC<{ d?: string; w?: number; children?: React.ReactNode }> = ({ d, w = 2, children }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={w}
       strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {d ? <path d={d} /> : children}
  </svg>
);
const IconBack = () => <Svg d="M19 12H5M11 6l-6 6 6 6" />;
const IconClose = () => <Svg d="M18 6 6 18M6 6l12 12" />;
const IconPin = () => (
  <svg className="pin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
       strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" /><circle cx="12" cy="10" r="3" />
  </svg>
);
const IconCopy = () => (
  <Svg w={1.9}><rect x="9" y="9" width="12" height="12" rx="2" /><path d="M5 15V5a2 2 0 0 1 2-2h10" /></Svg>
);
const IconCheck = () => <Svg d="M5 13l4 4L19 7" w={2.4} />;
const IconMic = () => (
  <Svg w={1.8}><rect x="9" y="3" width="6" height="11" rx="3" /><path d="M5 11a7 7 0 0 0 14 0M12 18v3" /></Svg>
);
const IconMicOff = () => (
  <Svg w={1.9}>
    <path d="M15 9.3V6a3 3 0 0 0-6 0v5a3 3 0 0 0 4.5 2.6" />
    <path d="M5 11a7 7 0 0 0 10.4 6.1M19 11a7 7 0 0 1-.5 2.6M12 18v3" />
    <path d="M4 4l16 16" />
  </Svg>
);
const IconCam = () => (
  <Svg w={1.8}><path d="M15 10l4.5-2.5v9L15 14" /><rect x="3" y="6" width="12" height="12" rx="2" /></Svg>
);
const IconPlay = () => <Svg d="M8 6l11 6-11 6V6Z" w={1.9} />;
const IconStop = () => <Svg w={1.9}><rect x="6" y="6" width="12" height="12" rx="2" /></Svg>;
const IconLaser = () => <Svg d="M4 3l7 17 2.5-6.5L20 11 4 3Z" w={1.8} />;
const IconEye = () => (
  <Svg w={1.8}><path d="M2 12s3.6-6.5 10-6.5S22 12 22 12s-3.6 6.5-10 6.5S2 12 2 12Z" /><circle cx="12" cy="12" r="2.6" /></Svg>
);
const IconHand = () => <Svg d="M8 13V5.5a1.5 1.5 0 0 1 3 0V12m0-1.5a1.5 1.5 0 0 1 3 0V12m0-1a1.5 1.5 0 0 1 3 0v4a6 6 0 0 1-6 6h-1a6 6 0 0 1-6-6v-2a1.5 1.5 0 0 1 3 0" w={1.8} />;
const IconPeople = () => (
  <Svg w={1.8}>
    <circle cx="9" cy="8" r="3.2" /><path d="M3 19a6 6 0 0 1 12 0" />
    <path d="M16 5.5a3.2 3.2 0 0 1 0 6M18 19a6 6 0 0 0-2.5-4.9" />
  </Svg>
);
const IconChat = () => <Svg d="M20 15a2 2 0 0 1-2 2H8l-4 4V6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2Z" w={1.8} />;
const IconSend = () => <Svg d="M4 12l16-8-6 16-2.5-6.5L4 12Z" w={1.8} />;
const IconRec = () => (
  <Svg w={1.8}><circle cx="12" cy="12" r="8.2" /><circle cx="12" cy="12" r="3.4" fill="currentColor" stroke="none" /></Svg>
);
const IconKick = () => (
  <Svg><path d="M10 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5" /><path d="M16 17l5-5-5-5M21 12H9" /></Svg>
);
const IconSearch = () => <Svg w={2}><circle cx="11" cy="11" r="7" /><path d="M20 20l-4.2-4.2" /></Svg>;

/* ── tier preview (localhost only) ───────────────────────────────────── */
const DEMO_POOL: Array<{ n: string; s: string; t: RoleKey }> = [
  { n: "Sunita Choudhary", s: "+91 98115 34062", t: "unlead" },
  { n: "Vivek Anand", s: "+91 96320 74418 · captured in tour", t: "fresh" },
  { n: "Ishita Roy", s: "+91 98300 55491", t: "unlead" },
  { n: "Sneha Kulkarni", s: "+91 90140 26653 · captured in tour", t: "fresh" },
  { n: "Rohan Pillai", s: "+91 99451 20876", t: "unlead" },
  { n: "Ritu Agarwal", s: "+91 98867 51092 · captured in tour", t: "fresh" },
  { n: "Manish Gupta", s: "+91 98730 41265", t: "unlead" },
  { n: "Zoya Ansari", s: "+91 99670 41285", t: "unlead" },
];
const DEMO_DESC: Record<number, string> = {
  0: "Empty lobby — the queue section hides and the stage reclaims the full width.",
  1: "A single floating card with the person's full detail and Admit / Deny.",
  3: "Up to three cards stack on the right — each still decided individually.",
  5: "One card holds a compact scrollable list: admit person-by-person, or all at once.",
  8: "Too many to read — collapses to a counted summary with Admit all and Review.",
};
const demoRows = (n: number): Participant[] =>
  DEMO_POOL.slice(0, n).map((p, i) => ({
    session_id: `demo-${i}`,
    /* Demo rows never join a channel, so nothing will ever be speaking as
       them — but the field is required and must not be undefined. */
    rtc_uid: `demo-${i}`,
    display_name: p.n,
    role: p.t,
    detail: p.s,
    lobby_status: "WAITING",
    __demo: true,
  }));

const GuidedRoomHost: React.FC = () => {
  const { roomId = "" } = useParams();
  const navigate = useNavigate();

  /* Notch insets for the caps and dock, and the preview harness overrides.
     See docs/product specs/responsive/RESPONSIVE_SPEC.md section 2. */
  useViewportInsets();

  const [room, setRoom] = useState<any>(null);
  const [project, setProject] = useState<any>(null);
  const [people, setPeople] = useState<Participant[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [viewerRole, setViewerRole] = useState<ViewerRole>("host");
  const [denied, setDenied] = useState(false);
  const isHost = viewerRole === "host";

  const [copied, setCopied] = useState(false);
  const [ending, setEnding] = useState(false);
  const [confirmEnd, setConfirmEnd] = useState(false);
  const [sheet, setSheet] = useState<null | "people" | "chat">(null);
  const [query, setQuery] = useState("");
  const [elapsed, setElapsed] = useState(0);
  /* Per-row spinner keyed by session_id — a single global `busy` would
     grey out the whole list while one person is being admitted, and the
     host is usually admitting several people in quick succession. */
  const [rowBusy, setRowBusy] = useState<Record<string, boolean>>({});

  const [demoQueue, setDemoQueue] = useState<number | null>(null);
  const [twOpen, setTwOpen] = useState(false);
  /* The buyer's-eye panel. OFF by default and unmounted when off, because
     it costs a second engine instance in this tab — see the panel below. */
  const [mirror, setMirror] = useState(false);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [sendFailed, setSendFailed] = useState(false);
  const logRef = useRef<HTMLDivElement | null>(null);

  /* The host's participant credential, written once at create time and
     never reissued. Read from storage rather than fetched because there
     is no endpoint that hands it back — losing it costs this browser its
     voice, and nothing else on the page.

     Read lazily and held in state so a re-render doesn't re-parse
     storage, and so the identity handed to the voice hook is referentially
     stable — the hook's effect keys on the credential, and a fresh object
     every render would make it join, leave and rejoin forever. */
  const [hostCred, setHostCred] = useState<{ session_id: string; session_secret: string } | null>(null);
  useEffect(() => {
    if (!roomId) return;
    try {
      const raw = localStorage.getItem(`${HOST_CRED_KEY}:${roomId}`);
      const c = raw ? JSON.parse(raw) : null;
      if (c?.session_id && c?.session_secret) {
        setHostCred({ session_id: c.session_id, session_secret: c.session_secret });
      }
    } catch { /* storage blocked — the page runs fine without voice */ }
  }, [roomId]);

  const status = String(room?.status || room?.state || "").toUpperCase();
  const live = status === "LIVE";
  const joinCode = room?.join_code || room?.room?.join_code || "";
  /* `started_at` ONLY — never `created_at`. Per the data model those are
     different moments: a room can sit SCHEDULED or LOBBY_OPEN for hours
     before the call begins, and anchoring to creation made the header
     count the wait rather than the tour. No start yet = no timer. */
  const startedAt = room?.started_at || room?.startedAt || "";
  const joinUrl = joinCode ? `${window.location.origin}/visit/${joinCode}` : "";

  /* Voice. Gated on the room being loaded and not denied — joining an
     Agora channel for a room we were refused, or for a room that turned
     out not to exist, would be a pointless round trip and a confusing
     error. Everything else (which channel, which uid, whether we may
     publish) is decided by the backend and arrives with the token. */
  const voice = useGuidedRoomVoice(roomId, hostCred, !!room && !denied);

  /* Project identity for the header. The room document carries only
     `project_id` — it has never carried a denormalised name — so an
     earlier version read `room.project_name`, always missed, and fell
     back to the literal "Guided tour" as the title.

     Resolved the same way GuidedRoomJoin does it, deliberately: same
     endpoint, same field paths, so the host and the buyer never see the
     project described two different ways. `project_name`, not `name` —
     see the @returns JSDoc on getPublicProject. */
  const projectId = room?.project_id || room?.project?.project_id || "";

  const projectName = project?.project_name || room?.project_name || "Guided tour";

  /* The walkthrough URL for THIS host — role=guide, so the engine
     broadcasts their camera instead of following someone else's. Empty
     when the project carries no meta model, which the stage handles by
     keeping its card. */
  const canvas = useMemo(
    () => canvasUrl(primarySource(project), { projectId, roomId, role: "guide" }),
    [project, projectId, roomId],
  );

  /* ── THE SAME ROOM, SEEN AS A BUYER SEES IT ──
     The host's own canvas is role=guide: it carries the waypoint strip,
     the minimap, the tier rail — the driving controls. A buyer's canvas
     is role=viewer, which the engine renders bare, following whatever
     this browser broadcasts. Those two pictures are not the same, and
     until now the host had no way to know what the other side actually
     had on screen.

     This is that second picture: the identical URL with role=viewer,
     joined to the same `room`. It is not a video feed of anyone's
     device — it is our own viewer instance obeying our own broadcast,
     which is exactly what every buyer's instance is doing. What it shows
     is what they see, arrived at the same way. Which also means it is an
     honest test of the sync itself: if this panel doesn't move with the
     host, neither does the room. */
  const guestCanvas = useMemo(
    () => canvasUrl(primarySource(project), { projectId, roomId, role: "viewer" }),
    [project, projectId, roomId],
  );

  /* Never leave it mounted across a stop — a viewer engine broadcasting
     nothing, in a room that ended, is pure cost. */
  useEffect(() => { if (!live) setMirror(false); }, [live]);

  const projectLocation = (() => {
    if (room?.project_location) return room.project_location;
    const a = project?.address;
    return [a?.landmark, a?.city].filter(Boolean).join(", ");
  })();

  const projectConfig = (() => {
    if (room?.project_configuration) return room.project_configuration;
    const units: any[] = project?.project_type_details?.unit_configurations || [];
    const bhk = Array.from(new Set(
      units
        .map((u: any) => Number(u?.bhk ?? u?.bhk_count ?? u?.configuration ?? NaN))
        .filter((n: number) => Number.isFinite(n) && n > 0),
    )).sort((a: any, b: any) => a - b) as number[];
    if (!bhk.length) return "";
    const raw = String(project?.project_type_details?.type || "").toLowerCase();
    const noun = raw.startsWith("villa") ? "Villas"
      : raw.startsWith("commercial") ? "Units"
        : "Apartments";
    const list = bhk.length <= 2
      ? bhk.join(" & ")
      : `${bhk.slice(0, -1).join(", ")} & ${bhk[bhk.length - 1]}`;
    return `${list} BHK ${noun}`;
  })();

  const waitingReal = useMemo(
    () => people.filter((p) => p.lobby_status === "WAITING"),
    [people],
  );
  /* The preview REPLACES the real queue rather than adding to it, so the
     host can never act on a mix of real and imaginary people. */
  const waiting = demoQueue === null ? waitingReal : demoRows(demoQueue);
  const inRoom = useMemo(
    () => people.filter((p) => p.lobby_status === "ADMITTED"),
    [people],
  );

  /* ── Raised hands ────────────────────────────────────────────────────
     Buyers announce these over the sync relay; see
     utils/guidedRoomRelay.ts for why the frames look the way they do.
     Keyed by session_id, which is what the rows below are keyed by, so a
     hand maps onto a person with nothing to look up. */
  const [hands, setHands] = useState<Hands>({});

  const onHand = useCallback((sig: { sessionId: string; raised: boolean }) => {
    setHands((prev) => applyHand(prev, sig as any, Date.now()));
  }, []);

  /* Chat over the voice channel the room already holds — see
     utils/guidedRoomChat.ts for why it can't ride the sync relay next to
     the raised hand. */
  const chat = useGuidedRoomChat({
    client: voice.client,
    sessionId: hostCred?.session_id || "",
    displayName: UserService.getFullName() || "Host",
    role: "guide",
    enabled: !!hostCred && !!room && !denied,
    /* The advisor's own messages are part of the tour's record too. */
    credential: hostCred,
    roomId,
    hostName: UserService.getFullName() || "",
  });

  const relay = useGuidedRoomRelay(roomId, hostCred?.session_id || "", "guide", {
    enabled: !!hostCred && !!room && !denied,
    /* The host credential is what buys a token carrying "drive" — the
       privilege that lets this browser move everyone else’s camera. */
    credential: hostCred,
    onHand,
  });

  /* Someone who left can't still be asking. The roster poll is the
     authority on who's here, so the queue is reconciled against it
     rather than trusting a "hand down" frame to arrive from a browser
     that may have been closed mid-sentence. */
  useEffect(() => {
    setHands((prev) => pruneHands(prev, inRoom.map((p) => p.session_id)));
  }, [inRoom]);

  /* Answering someone puts their hand down on both sides. Cleared here
     immediately rather than waiting for their client to confirm — the
     host clicked it, so it goes. */
  const answerHand = useCallback((sessionId: string) => {
    setHands((prev) => {
      if (!Object.prototype.hasOwnProperty.call(prev, sessionId)) return prev;
      const next = { ...prev };
      delete next[sessionId];
      return next;
    });
    relay.sendAck(sessionId);
  }, [relay]);

  const raisedIds = useMemo(() => handQueue(hands), [hands]);

  /* A clock for the wait labels. The room's own timer only starts when
     the tour goes live, and hands can go up before that — a buyer
     admitted early with a question shouldn't have their wait frozen at
     "just now". Fifteen seconds is finer than the minute granularity the
     labels show, and it only runs while a hand is actually up. */
  const [handClock, setHandClock] = useState(() => Date.now());
  useEffect(() => {
    if (Object.keys(hands).length === 0) return undefined;
    setHandClock(Date.now());
    const id = setInterval(() => setHandClock(Date.now()), 15000);
    return () => clearInterval(id);
  }, [hands]);

  /* Room document is fetched once — the polling loop only refreshes the
     lobby, which is the part that actually changes second to second. */
  useEffect(() => {
    if (!roomId) return undefined;
    let cancelled = false;
    getGuidedRoom(roomId)
      .then((r: any) => {
        if (cancelled) return;
        setRoom(r);
        /* The backend is the authority — it knows the room's host and its
           invite list, and derives the answer from the token. Until it
           returns `viewer_role`, fall back to "did this browser create the
           room": the host credential is only ever written on create. That
           degrades to view-only for a colleague opening the link, which is
           the safe direction to be wrong in. */
        const declared = String(r?.viewer_role || "").toLowerCase();
        if (declared === "host" || declared === "staff") {
          setViewerRole(declared as ViewerRole);
        } else {
          let hasHostCred = false;
          try {
            hasHostCred = !!localStorage.getItem(`${HOST_CRED_KEY}:${roomId}`);
          } catch { /* storage blocked — assume staff */ }
          setViewerRole(hasHostCred ? "host" : "staff");
        }
      })
      .catch((e: any) => {
        if (cancelled) return;
        /* 403 is a real answer, not a failure: authenticated, but not on
           this room's list. */
        if (e?.response?.status === 403) setDenied(true);
        else setErr("Couldn't load this room.");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [roomId]);

  /* Real project details for the header. Public endpoint, no auth — the
     same one GuidedRoomJoin and the share page use. Silent on failure:
     the header degrades to the join code and timer rather than blocking
     a live tour on a secondary fetch. */
  useEffect(() => {
    if (!projectId) return undefined;
    let cancelled = false;
    /* await rather than .then — the JSDoc @returns on getPublicProject
       declares the resolved shape, so TypeScript doesn't see a Promise. */
    void (async () => {
      try {
        const p: any = await getPublicProject(projectId);
        if (!cancelled && p) setProject(p);
      } catch { /* header degrades — nothing to tell the host mid-tour */ }
    })();
    return () => { cancelled = true; };
  }, [projectId]);

  /* The room's own invite list, used to tell an invited buyer from one who
     arrived on a forwarded link. The API now takes the whole list as a
     top-level `invitees` array of {id, kind} — `primary_lead_id` is gone
     as of the 2026-08-19 contract. Ids are read from whichever the room
     doc exposes; names only ever come from `settings.invitees`, since
     `Invitee` carries no name (see the create payload in ProjectDetailsV3).

     Matched on id where the lobby row carries one, and on name otherwise —
     both sides ultimately come from the same customer record, so the names
     agree. Null until the room has loaded, which keeps the first poll from
     labelling the host's own invitees "Uninvited". */
  const inviteCtx = useMemo<InviteCtx | undefined>(() => {
    if (!room) return undefined;
    const settingsInvitees: any[] = Array.isArray(room?.settings?.invitees)
      ? room.settings.invitees : [];
    const topInvitees: any[] = Array.isArray(room?.invitees) ? room.invitees : [];
    /* Names live in settings, ids can come from either — union the two so
       an id the backend echoes back is honoured even if settings didn't
       round-trip. */
    const raw = settingsInvitees.length ? settingsInvitees : topInvitees;
    const ids = new Set<string>(
      [...topInvitees, ...settingsInvitees, { id: room?.primary_lead_id }]
        .map((i: any) => i?.id).filter(Boolean).map(String),
    );
    const names = new Set<string>(
      raw.map((i: any) => String(i?.name || "").trim().toLowerCase()).filter(Boolean),
    );
    return {
      isInvited: (r: any) => {
        const id = String(r?.lead_id || r?.customer_id || r?.participant_id || "");
        if (id && ids.has(id)) return true;
        const nm = String(r?.display_name || r?.name || "").trim().toLowerCase();
        return !!nm && names.has(nm);
      },
      nameForId: (id: string) => {
        const key = String(id || "").trim();
        if (!key) return "";
        const hit = [...topInvitees, ...settingsInvitees]
          .find((i: any) => String(i?.id || "") === key);
        return String(hit?.name || "").trim();
      },
      selfName: UserService.getFullName() || "",
      /* Matches the design's "You · agent". */
      selfTitle: UserService.hasRole("agency_business_admin") ? "business admin"
        : UserService.hasRole("agency_system_admin") ? "system admin"
          : UserService.hasRole("agent") ? "agent" : "host",
    };
  }, [room]);

  const refreshLobby = useCallback(async () => {
    if (!roomId) return;
    /* Wait for the room, because the invite list is what classifies each
       row. Polling first would paint every buyer with the fallback label
       for a frame — and "Invited lead" is exactly the claim we must not
       make on a guess. The poll effect re-fires when this resolves. */
    if (!inviteCtx) return;
    try {
      const res = await getGuidedRoomLobby(roomId);
      setPeople(normalizeLobby(res, inviteCtx));
      setErr("");
    } catch (e: any) {
      /* A transient poll failure shouldn't wipe the list the host is
         mid-way through actioning — surface it, keep the last good data. */
      if (e?.response?.status === 404) setErr("This room no longer exists.");
    }
  }, [roomId, inviteCtx]);

  const pollRef = useRef<any>(null);
  useEffect(() => {
    if (!roomId || status === "ENDED") return undefined;
    void refreshLobby();
    pollRef.current = setInterval(refreshLobby, POLL_MS);
    return () => clearInterval(pollRef.current);
  }, [roomId, status, refreshLobby]);

  /* Header timer. Anchored to the room's own start so a refresh doesn't
     restart it — a counter that resets when the host reloads is worse
     than none, since it's the thing they glance at to pace a tour. */
  useEffect(() => {
    if (!startedAt) return undefined;
    const t0 = utcDate(startedAt);
    if (isNaN(t0)) return undefined;
    const tick = () => setElapsed((Date.now() - t0) / 1000);
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [startedAt]);

  const withRowBusy = async (sessionId: string, fn: () => Promise<any>) => {
    setRowBusy((m) => ({ ...m, [sessionId]: true }));
    try {
      await fn();
      await refreshLobby();
    } catch (e: any) {
      setErr(apiMessage(e));
    }
    setRowBusy((m) => ({ ...m, [sessionId]: false }));
  };

  /* Demo rows have no server-side counterpart — resolve them locally so
     the tier preview behaves like the real thing without 404ing. */
  const dropDemo = (p: Participant) =>
    setDemoQueue((n) => (n === null ? n : Math.max(0, n - 1)));

  const admit = (p: Participant) =>
    p.__demo
      ? dropDemo(p)
      : withRowBusy(p.session_id, () => admitToGuidedRoom(roomId, p.session_id, "admit"));

  const reject = (p: Participant) =>
    p.__demo
      ? dropDemo(p)
      : withRowBusy(p.session_id, () => admitToGuidedRoom(roomId, p.session_id, "reject"));

  const toggleMute = (p: Participant) =>
    withRowBusy(p.session_id, () =>
      manageParticipant(roomId, p.session_id, p.muted ? "unmute" : "mute"));

  const remove = (p: Participant) =>
    withRowBusy(p.session_id, () => manageParticipant(roomId, p.session_id, "remove"));

  const admitAll = async () => {
    if (demoQueue !== null) { setDemoQueue(0); return; }
    for (const p of waitingReal) {
      // Sequential on purpose — the backend assigns seats, and firing six
      // admits at once risks racing past the room cap.
      // eslint-disable-next-line no-await-in-loop
      await withRowBusy(p.session_id, () =>
        admitToGuidedRoom(roomId, p.session_id, "admit"));
    }
  };

  /* Everyone but the host. "Mute all" that also muted the host would cut
     the voice the room came for. */
  const mutable = inRoom.filter((p) => p.role !== "host");
  const allMuted = mutable.length > 0 && mutable.every((p) => p.muted);
  const muteAll = async () => {
    for (const p of mutable) {
      if (!!p.muted === !allMuted) continue;
      // eslint-disable-next-line no-await-in-loop
      await withRowBusy(p.session_id, () =>
        manageParticipant(roomId, p.session_id, allMuted ? "unmute" : "mute"));
    }
  };

  /* The PATCH action vocabulary is the one thing here not pinned down in
     either spec doc ("open; cancel" is all they say), so treat a failure
     as non-fatal: the lobby and admit flow work regardless of whether the
     room is LOBBY_OPEN or LIVE. Confirm the strings with Nivedita. */
  /* Opening the panel is reading it; a badge that survives an open
     panel reads as broken. */
  useEffect(() => {
    if (sheet === "chat") chat.markRead();
  }, [sheet, chat.messages.length]);            // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (sheet !== "chat") return;
    const el = logRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [chat.messages.length, sheet]);

  /* ── WHO SENT IT: THE ROSTER, NOT THE MESSAGE ──
     A chat frame carries the name the SENDER knew about themselves at the
     moment they typed. That is often nothing useful: a buyer's own lobby row
     arrives on a five-second poll, so anything sent in the first seconds went
     out labelled "Guest" — which is how a real lead ends up appearing as
     GUEST next to their own words.

     The roster is the authority, it is the same list the People tab renders,
     and it is keyed by session_id exactly as messages are. So the name is
     resolved here, at render time, rather than trusted from the wire. Three
     consequences worth having: a message already on screen gets the right
     name the moment the roster catches up, everyone in the room sees the same
     name for the same person, and a sender cannot label themselves whatever
     they like. The frame's own name is the fallback for a participant who has
     since left and dropped off the roster. */
  const nameFor = useCallback((sessionId: string, fallback: string) => {
    const row = people.find((p) => String(p.session_id) === String(sessionId));
    return (row?.display_name || "").trim() || fallback || "Guest";
  }, [people]);

  /* And WHICH of them it is. With a lead, two family members and a colleague
     in one room, a bare name doesn't tell a host whether the question they
     just got came from the buyer or from the buyer's brother-in-law — and
     those deserve different answers. Same vocabulary as the People tab, so
     the two panels never describe one person two ways. Silent for the host's
     own messages, which are already labelled "You". */
  const roleFor = useCallback((sessionId: string) => {
    const row = people.find((p) => String(p.session_id) === String(sessionId));
    const key = row?.role as RoleKey | undefined;
    return key && key !== "host" ? R[key]?.l || "" : "";
  }, [people]);

  const onSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!draft.trim() || sending) return;
    setSending(true);
    const ok = await chat.send(draft);
    setSending(false);
    setSendFailed(!ok);
    if (ok) setDraft("");
  };

  const toggleWalkthrough = async () => {
    try {
      await patchGuidedRoom(roomId, { action: live ? "stop" : "start" });
      const r = await getGuidedRoom(roomId);
      setRoom(r);
    } catch {
      setErr("Couldn't change the walkthrough state — check the action name with the backend.");
    }
  };

  /* "cannot 'end' from state 'ENDED'" is not a failure. The host asked for
     the tour to be over and the tour is over — saying so in the backend's
     words, in red, over a room they just closed, only makes them wonder
     what they broke. Treat it as the success it is.

     It happens for an ordinary reason: the host ends the tour, the room
     page still shows End tour, and they press it again when something
     else on screen hasn't caught up yet. */
  const alreadyEnded = (e: any): boolean => {
    const msg = apiMessage(e, "").toLowerCase();
    return msg.includes("ended") && (msg.includes("cannot") || msg.includes("state"));
  };

  const end = async () => {
    setEnding(true);
    try {
      try {
        await endGuidedRoom(roomId);
      } catch (e: any) {
        if (!alreadyEnded(e)) throw e;
      }
      releaseRoom(roomId);
      /* Opened via window.open from the project page, so there's usually
         no history to go back to — close the tab instead, and fall back to
         a redirect when the browser refuses (only tabs opened by script
         may close themselves). */
      window.close();
      window.setTimeout(() => { if (!window.closed) navigate("/"); }, 300);
    } catch (e: any) {
      setErr(apiMessage(e, "Couldn't end the tour."));
      setEnding(false);
      setConfirmEnd(false);
    }
  };

  /* ── RECORDING ──
     Was a disabled placeholder ("Recording isn't wired up yet") until
     2026-09-11. It matters more than it looks: recording only auto-starts
     for a SCHEDULED room, and every room the project page makes is
     instant, so without this button no tour was ever recorded. Decisions
     live in utils/guidedRoomRecording; this is wiring. Polled while live
     because the backend moves the row on its own (STARTING → RECORDING,
     or FAILED with a reason) and the host should see that, not guess. */
  const [recLatest, setRecLatest] = useState<RecordingBrief | null>(null);
  const [recBusy, setRecBusy] = useState<"start" | "stop" | null>(null);
  const [recProblem, setRecProblem] = useState("");
  const [autoAdmitBusy, setAutoAdmitBusy] = useState(false);

  const refreshRecordings = useCallback(async () => {
    if (!roomId) return;
    try {
      setRecLatest(latestRecording(await listGuidedRoomRecordings(roomId)));
    } catch { /* keep the last known state — a missed poll isn't news */ }
  }, [roomId]);

  useEffect(() => {
    if (!isHost || !live) return undefined;
    void refreshRecordings();
    const id = setInterval(() => { void refreshRecordings(); }, 10000);
    return () => clearInterval(id);
  }, [isHost, live, refreshRecordings]);

  const recDock = recordingDock({
    live,
    enabled: room?.settings?.recording_enabled === true,
    latest: recLatest,
    busy: recBusy,
    problem: recProblem,
  });

  const toggleRecording = async () => {
    const action = recDock.action;
    if (!action) return;
    setRecBusy(action);
    setRecProblem("");
    try {
      if (action === "start") await startGuidedRoomRecording(roomId);
      else await stopGuidedRoomRecording(roomId);
    } catch (e: any) {
      /* The raw reason goes to the console for whoever debugs it; the
         host gets words they can act on, on the button itself. */
      // eslint-disable-next-line no-console
      console.warn("[GuidedRoom] recording", action, e?.response?.status, apiMessage(e, ""));
      setRecProblem(recordingProblem(e));
    } finally {
      setRecBusy(null);
      void refreshRecordings();
    }
  };

  /* "Let new people straight in" — only rendered once the room carries
     the setting (backend item 32); see autoAdmitSetting. */
  const autoAdmit = autoAdmitSetting(room);
  const toggleAutoAdmit = async () => {
    if (!autoAdmit.available || autoAdmitBusy) return;
    setAutoAdmitBusy(true);
    try {
      await patchGuidedRoom(roomId, { settings: { [AUTO_ADMIT_KEY]: !autoAdmit.on } });
      setRoom(await getGuidedRoom(roomId));
    } catch (e: any) {
      setErr(apiMessage(e, "Couldn't change who gets let in."));
    } finally {
      setAutoAdmitBusy(false);
    }
  };

  const copyLink = () => {
    if (!joinUrl) return;
    try { navigator.clipboard.writeText(joinUrl); } catch { /* no-op */ }
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  /* Esc closes the sheet — the room is full-screen with no chrome to
     click away to, so a keyboard exit is the only reliable one. */
  useEffect(() => {
    if (!sheet) return undefined;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setSheet(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [sheet]);

  const matches = (p: Participant) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return `${p.display_name} ${R[p.role].l} ${p.detail}`.toLowerCase().includes(q);
  };
  const waitingShown = waiting.filter(matches);
  const inRoomShown = inRoom.filter(matches);

  if (loading) {
    return (
      <div className="grh-root grh-root--center">
        <span className="grh-spinner" aria-hidden="true" />
      </div>
    );
  }

  /* Logged in, but not the host and not on the invite list. Deliberately
     says nothing about the room — a staff member from another agency
     shouldn't learn the project name by trying a room id. */
  if (denied) {
    return (
      <div className="grh-root grh-root--center">
        <div className="grh-denied">
          <h1>You're not on this tour</h1>
          <p>Ask the host to add you, then open the link again.</p>
          <button className="pill" onClick={() => navigate("/")}>Back to projects</button>
        </div>
      </div>
    );
  }

  /* The stage gutters for whichever right-hand column is showing. */
  const gutter = (isHost && waiting.length > 0) || !!sheet;

  const detailLine = (p: Participant, withRole = true) => (
    <small>
      {withRole && p.role !== "staff" && (
        /* `.rl::after` supplies the " · " separator, so it has to go when
           there's nothing after it — a row whose detail hasn't arrived
           otherwise renders "Host ·" trailing into space. */
        <span className={`rl ${R[p.role].c}${p.detail ? "" : " bare"}`}>{R[p.role].l}</span>
      )}
      {p.detail}
    </small>
  );

  const avatar = (p: Participant, dot = false) => (
    <span className={`av${R[p.role].a ? ` ${R[p.role].a}` : ""}`}>
      {dot && <span className="st" />}
      {initial(p.display_name)}
    </span>
  );

  /* ── the lobby, sized to itself ─────────────────────────────────────
     A queue of 2 and a queue of 40 are different design problems. The
     4–5 tier is the largest at which reading names one by one still
     beats a count and a bulk action. */
  const renderRequests = () => {
    if (!isHost || waiting.length === 0) return null;

    if (waiting.length <= 3) {
      return (
        <div className="reqs">
          {waiting.map((p) => (
            <div className={`req${p.role === "fresh" ? " sum" : ""}`} key={p.session_id}>
              <div className="req-t">
                {avatar(p)}
                <div className="who">
                  <b>{p.display_name}</b>
                  <small>{p.detail}</small>
                </div>
                <span className={`tag ${R[p.role].c}`}>{R[p.role].l}</span>
              </div>
              <div className="req-a">
                <button className="ok" disabled={rowBusy[p.session_id]} onClick={() => void admit(p)}>
                  {rowBusy[p.session_id] ? "…" : "Admit"}
                </button>
                <button className="no" disabled={rowBusy[p.session_id]} onClick={() => void reject(p)}>
                  Deny
                </button>
              </div>
            </div>
          ))}
        </div>
      );
    }

    if (waiting.length <= 5) {
      return (
        <div className="reqs">
          <div className="req sum">
            <div className="req-t">
              <span className="who">
                <b>{waiting.length} people waiting</b>
                <small>Admit individually, or all at once</small>
              </span>
            </div>
            <div className="qlist">
              {waiting.map((p) => (
                <div className="qmini" key={p.session_id}>
                  {avatar(p)}
                  <span className="who">
                    <b>{p.display_name}</b>
                    {detailLine(p)}
                  </span>
                  <button className="ok mini2" disabled={rowBusy[p.session_id]}
                          onClick={() => void admit(p)}>
                    {rowBusy[p.session_id] ? "…" : "Admit"}
                  </button>
                </div>
              ))}
            </div>
            <div className="req-a">
              <button className="ok" onClick={() => void admitAll()}>Admit all {waiting.length}</button>
              <button className="no" onClick={() => setSheet("people")}>Review</button>
            </div>
          </div>
        </div>
      );
    }

    /* 6+ — too many to read. A count and a bulk action beat a wall of
       names. The breakdown only appears when the backend actually
       distinguishes the gated roles; otherwise it says nothing rather
       than inventing a split. */
    const n = (t: RoleKey) => waiting.filter((p) => p.role === t).length;
    const parts: string[] = [];
    if (n("unlead")) parts.push(`${n("unlead")} forwarded`);
    if (n("fresh")) parts.push(`${n("fresh")} new number${n("fresh") === 1 ? "" : "s"}`);
    return (
      <div className="reqs">
        <div className="req sum">
          <div className="req-t">
            <span className="stack">
              {waiting.slice(0, 3).map((p) => (
                <span className={`sq${R[p.role].a ? ` ${R[p.role].a}` : ""}`} key={p.session_id}>
                  {initial(p.display_name)}
                </span>
              ))}
            </span>
            <div className="who">
              <b>{waiting.length} people waiting</b>
              {parts.length > 0 && <small>{parts.join(" · ")}</small>}
            </div>
          </div>
          <div className="req-a">
            <button className="ok" onClick={() => void admitAll()}>Admit all</button>
            <button className="no" onClick={() => setSheet("people")}>Review</button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="grh-root">
      <div className="stage">
        <div className="stage-grid" />
        <div className={`mount${gutter ? " gutter" : ""}`}>
          <div className="mount-in" hidden={live}>
            <div className="mount-mark"><IconCam /></div>
            <h2>Ready when you are</h2>
            <p>
              Hit <b>Start walkthrough</b> to load the experience and put it on
              everyone's screen at once — you drive, they follow.
            </p>
          </div>
          {/* Live, and this project has something to render: the engine
              with role=guide. The host drives; every viewer in the same
              `room` follows over the sync relay. Mounted only while live
              so the walkthrough doesn't start loading — or broadcasting a
              camera — while the host is still admitting people.

              See utils/guidedRoomCanvas.ts for why this URL carries less
              than ProjectDetailsV3's. */}
          {live && canvas ? (
            <iframe
              className="canvas"
              title={`Guided walkthrough of ${projectName || "the project"}`}
              src={canvas}
              allow="fullscreen; xr-spatial-tracking; accelerometer; gyroscope"
              allowFullScreen
            />
          ) : (
            <div className="mount-in" hidden={!live}>
              <div className="mount-mark playing"><IconPlay /></div>
              <h2>Walkthrough is live</h2>
              <p>
                Mirroring your view to {inRoom.length === 1 ? "1 person" : `${inRoom.length} people`}.
                {" "}This project has no meta model to render, so the tour is
                voice and the project panel — everything else works.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* ── THE BUYER'S VIEW ──────────────────────────────────────────
          Mounted only while the host asks for it. Two engine instances in
          one tab is real cost — GPU, memory, and a second WebSocket on
          the relay — so this is a control the host reaches for when they
          want to check the other side, not a permanent second window.

          Deliberately not interactive: it is a monitor, and a host who
          drags inside it would move only this copy, see the two pictures
          disagree, and conclude the tour had desynchronised. */}
      {live && mirror && guestCanvas && (
        <div className={`mirror${gutter ? " gutter" : ""}`}>
          <div className="mirror-h">
            <span className="mirror-t"><i />What the buyer sees</span>
            <button className="icb" onClick={() => setMirror(false)} aria-label="Hide the buyer's view">
              <IconClose />
            </button>
          </div>
          <iframe
            className="mirror-c"
            title="The walkthrough as a buyer sees it"
            src={guestCanvas}
            allow="xr-spatial-tracking; accelerometer; gyroscope"
          />
          <p className="mirror-n">
            Their screen, following yours — no waypoints, no map, no controls.
          </p>
        </div>
      )}

      {err && <div className="grh-err">{err}</div>}
      {/* The advisor is the one person who must know their own connection
          has gone: they are mid-sentence, describing a room nobody can
          hear them describe. Uses the same slot as the error banner
          rather than inventing a second surface. */}
      {connectionNotice(voice.connection) && (
        <div className="grh-err" role="status">{connectionNotice(voice.connection)}</div>
      )}

      <div className="hud hud-tl">
        <div className="cap">
          <button className="icb" onClick={() => navigate(-1)} aria-label="Leave room">
            <IconBack />
          </button>
          <div className="meta">
            <b>{projectName}</b>
            <small>
              {projectConfig && <><span className="ptype">{projectConfig}</span><span className="dot" /></>}
              {projectLocation && <><IconPin />{projectLocation}<span className="dot" /></>}
              Guided tour
              {joinCode && <><span className="dot" /><span className="code">{joinCode}</span></>}
              {startedAt && <><span className="dot" /><span className="code">{clock(elapsed)}</span></>}
            </small>
          </div>
          {live && <span className="rec"><i />LIVE</span>}
          {recDock.recording && (
            <span className="rec rec-on" title="This tour is being recorded"><i />REC</span>
          )}
        </div>
      </div>

      <div className="hud hud-tr">
        <div className="cap">
          {isHost && (
            <button className="pill" onClick={copyLink} disabled={!joinUrl}>
              {copied ? <IconCheck /> : <IconCopy />}
              {copied ? "Copied" : "Invite"}
            </button>
          )}
          {isHost ? (
            <button className="pill pill-end" onClick={() => setConfirmEnd(true)} disabled={ending}>
              <IconClose />End tour
            </button>
          ) : (
            <button className="pill" onClick={() => navigate("/")}>Leave</button>
          )}
        </div>
      </div>

      {renderRequests()}

      {/* ── dock ──────────────────────────────────────────────────────
          Staff sit in; they don't run the room. Hiding the driving
          controls rather than disabling them: a greyed-out "End tour"
          invites a colleague to try, and the backend rejects it anyway. */}
      <div className="dock">
        {/* Mic. Four readings, and they are deliberately distinguishable
            — "my mic is off" and "voice never connected" look identical
            on a muted button, and a host who can't tell will keep talking
            into a room that can't hear them.
              · connecting → disabled, says so
              · error      → disabled, carries the reason
              · no cred    → disabled, explains this browser can't
              · live       → the real toggle, `on` when unmuted */}
        <button
          className={`ctl${voice.micOn ? " on" : ""}`}
          disabled={voice.status !== "live" || !voice.canPublish || !!voice.micError}
          aria-pressed={voice.micOn}
          aria-label={voice.micOn ? "Mute your microphone" : "Unmute your microphone"}
          data-tip={
            voice.status === "live"
              ? (!voice.canPublish
                ? "You're listening only"
                /* A mic that failed to start used to leave this reading
                   "Unmute" on a button that did nothing when pressed. */
                : voice.micError || (voice.micOn ? "Mute" : "Unmute"))
              : voice.status === "connecting" ? "Connecting voice…"
                : voice.error || (hostCred
                  ? "Voice unavailable"
                  : "Voice needs the browser that started this tour")
          }
          onClick={() => void voice.toggleMic()}
        >
          <IconMic />
          {voice.micOn && <span className="mic-live" aria-hidden="true" />}
        </button>
        {/* Not "coming soon" — a decision. See the header note. */}
        <button className="ctl" disabled data-tip="Voice only — video would compete with the property"><IconCam /></button>
        <div className="sep" />
        {isHost && (
          <>
            <button className={`ctl ctl-main${live ? " on" : ""}`} onClick={() => void toggleWalkthrough()}>
              {live ? <IconStop /> : <IconPlay />}
              {live ? "Stop walkthrough" : "Start walkthrough"}
            </button>
            <button
              className={`ctl${mirror ? " on" : ""}`}
              disabled={!live || !guestCanvas}
              aria-pressed={mirror}
              aria-label="See what the buyer sees"
              onClick={() => setMirror((v) => !v)}
              data-tip={
                !live ? "Start the walkthrough first"
                  : !guestCanvas ? "This project has no meta model to mirror"
                    : mirror ? "Hide the buyer's view" : "See what the buyer sees"
              }
            >
              <IconEye />
            </button>
            <button className="ctl" disabled data-tip="Laser needs the shared canvas"><IconLaser /></button>
            <div className="sep" />
          </>
        )}
        {/* Only exists while someone is waiting to speak. A permanent
            control that reads zero most of the time trains the host to
            stop looking at it, which defeats the purpose — this appears,
            so it gets noticed. */}
        {isHost && raisedIds.length > 0 && (
          <button
            className="ctl hands"
            onClick={() => setSheet("people")}
            data-tip={raisedIds.length === 1 ? "1 raised hand" : `${raisedIds.length} raised hands`}
            aria-label={raisedIds.length === 1 ? "1 raised hand" : `${raisedIds.length} raised hands`}
          >
            <IconHand />
            <span className="badge">{raisedIds.length}</span>
          </button>
        )}
        <button className="ctl" onClick={() => setSheet("people")} data-tip="People">
          <IconPeople />
          {isHost && waiting.length > 0 && <span className="badge">{waiting.length}</span>}
        </button>
        <button className="ctl" onClick={() => setSheet("chat")} data-tip="Chat">
          <IconChat />
          {chat.unread > 0 && <span className="badge">{chat.unread}</span>}
        </button>
        {isHost && (
          <button
            className={`ctl${recDock.pressed ? " rec-live" : ""}`}
            disabled={recDock.disabled}
            aria-pressed={recDock.pressed}
            aria-label={recDock.label}
            data-tip={recDock.tip}
            onClick={() => void toggleRecording()}
          >
            <IconRec />
          </button>
        )}
      </div>

      <div className={`scrim${sheet ? " on" : ""}`} onClick={() => setSheet(null)} />
      <aside className={`sheet${sheet ? " on" : ""}`}>
        <div className="sheet-h">
          <div className="tabs">
            <button className={`tab${sheet === "people" ? " on" : ""}`} onClick={() => setSheet("people")}>
              People <span className="n">{inRoom.length}</span>
            </button>
            <button className={`tab${sheet === "chat" ? " on" : ""}`} onClick={() => setSheet("chat")}>
              Chat
            </button>
          </div>
          <button className="icb" onClick={() => setSheet(null)} aria-label="Close panel">
            <IconClose />
          </button>
        </div>

        <div className="sheet-b">
          <div className={`pane${sheet === "people" ? " on" : ""}`}>
            <div className="srch">
              <IconSearch />
              <input value={query} onChange={(e) => setQuery(e.target.value)}
                     placeholder="Search people…" aria-label="Search people" />
            </div>

            <div className="plist">
              {isHost && autoAdmit.available && (
                <div className="autoadmit">
                  <span className="aa-t">
                    <b>Let new people straight in</b>
                    <small>
                      {autoAdmit.on
                        ? "New and uninvited leads skip the lobby."
                        : "New and uninvited leads wait here for you."}
                    </small>
                  </span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={autoAdmit.on}
                    aria-label="Let new people straight in"
                    className={`aa-sw${autoAdmit.on ? " on" : ""}`}
                    disabled={autoAdmitBusy}
                    onClick={() => void toggleAutoAdmit()}
                  >
                    <i />
                  </button>
                </div>
              )}
              {isHost && waiting.length > 0 && (
                <>
                  <p className="lbl">
                    Needs your approval · <span className="n2">{waiting.length}</span>
                    {waiting.length > 1 && (
                      <button className="lnk" onClick={() => void admitAll()}>Admit all</button>
                    )}
                  </p>
                  <div className="queue">
                    {waitingShown.map((p) => (
                      <div className={`qrow${p.role === "fresh" ? " fresh" : ""}`} key={p.session_id}>
                        {avatar(p)}
                        <div className="who">
                          <b>{p.display_name}</b>
                          {detailLine(p)}
                        </div>
                        <span className="qa">
                          <button className="y" disabled={rowBusy[p.session_id]}
                                  onClick={() => void admit(p)}>Admit</button>
                          <button className="x" disabled={rowBusy[p.session_id]}
                                  onClick={() => void reject(p)}
                                  aria-label={`Deny ${p.display_name}`}>×</button>
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              )}

              <p className="lbl">
                In the room · <span className="n2">{inRoom.length}</span>
                {isHost && mutable.length > 0 && (
                  <button className="lnk" onClick={() => void muteAll()}>
                    {allMuted ? "Unmute all" : "Mute all"}
                  </button>
                )}
              </p>
              {/* `talking` is keyed by the Agora uid, which IS the
                  participant's session_id — the backend issues the same
                  string for both, so the two lists line up without a
                  mapping table. It stays false for everyone until voice
                  connects, so the rings simply never appear rather than
                  all lighting at once. */}
              {inRoomShown.map((p) => (
                <div
                  className={`row${p.muted ? " quiet" : ""}${voice.speaking[p.rtc_uid || p.session_id] ? " talking" : ""}`}
                  key={p.session_id}
                >
                  {avatar(p, true)}
                  <div className="who">
                    <b>{p.display_name}</b>
                    {detailLine(p)}
                  </div>
                  {/* A raised hand, answerable in place. The wait time is
                      the point: a host mid-walkthrough needs to know
                      whether this is new or whether they've been leaving
                      someone hanging for four minutes. */}
                  {hands[p.session_id] && (
                    <button
                      className="handchip"
                      onClick={() => answerHand(p.session_id)}
                      title={`Answer ${firstNameOf(p.display_name)} — clears their raised hand`}
                      aria-label={`Answer ${p.display_name}, raised ${handWaitLabel(hands[p.session_id], handClock)}`}
                    >
                      <IconHand />
                      <span>{handWaitLabel(hands[p.session_id], handClock)}</span>
                    </button>
                  )}
                  {isHost && p.role !== "host" && (
                    <span className="acts">
                      <button className="mini" disabled={rowBusy[p.session_id]}
                              onClick={() => void toggleMute(p)}
                              title={p.muted ? `Unmute ${firstNameOf(p.display_name)}` : `Mute ${firstNameOf(p.display_name)}`}
                              aria-label={p.muted ? `Unmute ${p.display_name}` : `Mute ${p.display_name}`}>
                        {p.muted ? <IconMicOff /> : <IconMic />}
                      </button>
                      <button className="mini rm" disabled={rowBusy[p.session_id]}
                              onClick={() => void remove(p)}
                              title="Remove from tour"
                              aria-label={`Remove ${p.display_name}`}>
                        <IconKick />
                      </button>
                    </span>
                  )}
                </div>
              ))}

              {inRoom.length === 0 && !query && (
                <div className="hint">Nobody's in yet.</div>
              )}
              {query && waitingShown.length === 0 && inRoomShown.length === 0 && (
                <div className="hint">No one matches that search.</div>
              )}
              {/* States the rule, so an empty queue never reads as a bug. */}
              {isHost && waiting.length === 0 && (
                <div className="hint" style={{ marginTop: 14 }}>
                  No one waiting. Invited leads, family passes and your own team
                  join straight in — only forwarded links and new numbers wait here.
                </div>
              )}
            </div>
          </div>

          <div className={`pane${sheet === "chat" ? " on" : ""}`}>
            <div className="chatlog" ref={logRef}>
              {/* Said to the host as plainly as to the buyer: there is no
                  store behind this. A host who believes they can read a
                  tour's chat back tomorrow will promise something on it. */}
              <div className="cmsg sys">
                {/* Was "Nothing is saved after it ends", which stopped
                    being true when chat persistence started working on
                    2026-09-10. The buyer's copy carries the same
                    correction — see the note there for why a stale
                    reassurance about someone's own words is worse than a
                    stale fact about anything else. */}
                <p>Messages reach everyone in the room, and are kept with the tour record.</p>
              </div>
              {chat.messages.map((m) => {
                const mine = m.sessionId === (hostCred?.session_id || "");
                const stamp = chatTime(m.at);
                const role = mine ? "" : roleFor(m.sessionId);
                return (
                  <div className={`cmsg${mine ? " me" : ""}`} key={m.id}>
                    <b>
                      {mine ? "You" : nameFor(m.sessionId, m.name)}
                      {role ? <span className="crole">{role}</span> : null}
                      {stamp ? ` · ${stamp}` : ""}
                    </b>
                    <p>{m.text}</p>
                  </div>
                );
              })}
              {!!chat.unavailable && <div className="cmsg sys"><p>{chat.unavailable}</p></div>}
              {sendFailed && (
                <div className="cmsg sys"><p>That message didn&apos;t send. Try again.</p></div>
              )}
            </div>
            <form className="compose" onSubmit={(e) => void onSend(e)}>
              <input
                value={draft}
                onChange={(e) => { setDraft(e.target.value); setSendFailed(false); }}
                disabled={!chat.ready || sending}
                placeholder={
                  chat.unavailable ? chat.unavailable
                    : chat.ready ? "Message the room"
                      : "Connecting…"
                }
                aria-label="Message the room"
              />
              <button type="submit" disabled={!chat.ready || sending || !draft.trim()} aria-label="Send">
                <IconSend />
              </button>
            </form>
          </div>
        </div>
      </aside>

      {/* Localhost only. The five lobby tiers are the heart of this
          screen's design and there is no other way to see 6+ waiting
          without six real people; gated so it can never reach stage or
          prod, and it REPLACES the real queue rather than mixing in. */}
      {IS_LOCAL && isHost && (
        <>
          <button className="tw-fab" onClick={() => setTwOpen((v) => !v)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9"
                 strokeLinecap="round" aria-hidden="true">
              <line x1="4" y1="7" x2="20" y2="7" /><circle cx="9" cy="7" r="2.4" />
              <line x1="4" y1="17" x2="20" y2="17" /><circle cx="15" cy="17" r="2.4" />
            </svg>
            Tiers
          </button>
          <div className="tw" hidden={!twOpen}>
            <p className="tw-h">People waiting</p>
            <div className="tw-o">
              {[0, 1, 3, 5, 8].map((n) => (
                <button key={n} className={demoQueue === n ? "on" : ""}
                        onClick={() => setDemoQueue(n)}>
                  {n === 3 ? "2–3" : n === 5 ? "4–5" : n === 8 ? "6+" : n}
                </button>
              ))}
              <button className={demoQueue === null ? "on" : ""} onClick={() => setDemoQueue(null)}>
                Real
              </button>
            </div>
            <p className="tw-d">
              {demoQueue === null
                ? "Showing the room's actual queue."
                : DEMO_DESC[demoQueue]}
            </p>
          </div>
        </>
      )}

      {confirmEnd && (
        <div className="modal" role="dialog" aria-modal="true">
          <div className="modalcard">
            <h2>End this tour?</h2>
            <p>
              Everyone is disconnected and the room closes for good. The session
              is saved against {projectName}.
            </p>
            <div className="modalact">
              <button className="pill" onClick={() => setConfirmEnd(false)} disabled={ending}>
                Keep going
              </button>
              <button className="pill pill-end" onClick={() => void end()} disabled={ending}>
                {ending ? "Ending…" : "End tour"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GuidedRoomHost;
export { GuidedRoomHost };
